import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { NewEventDetailAttendeesGroupListItem } from "./NewEventDetailAttendeesGroupListItem";
import { NewEventDetailAttendeesListItem } from "./NewEventDetailAttendeesListItem";
import { NewEventDetailAttendeesGroupListItemFragment$key } from "./__generated__/NewEventDetailAttendeesGroupListItemFragment.graphql";
import { NewEventDetailAttendeesList$key } from "./__generated__/NewEventDetailAttendeesList.graphql";
import { NewEventDetailAttendeesListItemFragment$key } from "./__generated__/NewEventDetailAttendeesListItemFragment.graphql";

type NewEventDetailAttendeesListProps = {
  fragmentRef: NewEventDetailAttendeesList$key;
};

export const NewEventDetailAttendeesList = ({
  fragmentRef,
}: NewEventDetailAttendeesListProps) => {
  const data = useFragment(NewEventDetailAttendeesListFragment, fragmentRef);
  const eventPlayerNoun = getNounOverride(
    data.eventSeries?.facility?.copyOverrides?.eventPlayer?.singular,
    t`Player`
  );

  const isGroupEvent =
    data?.maxGuestCount !== 0 ||
    data?.maxMemberCount !== 1 ||
    data?.minMemberCount !== 1 ||
    data?.minGuestCount !== 0;

  const isGroupCapacity = data.capacityType === "GROUPS";

  const numAttendees =
    isGroupEvent && isGroupCapacity
      ? data?.eventSignUps?.edges?.length
      : data?.eventSignUps?.edges?.reduce(
          (a, b) => a + (b?.node?.registrations?.count ?? 0),
          0
        );

  const eventSignUpRows: {
    groupFragmentRef: NewEventDetailAttendeesGroupListItemFragment$key | null;
    fragmentRef: NewEventDetailAttendeesListItemFragment$key | null;
    id?: string;
  }[] =
    data.eventSignUps?.edges?.map((item) => ({
      id: item?.node?.id,
      fragmentRef: item?.node ?? null,
      groupFragmentRef: item?.node ?? null,
    })) || [];

  if (numAttendees === 0) return null;

  return (
    <Box px={5} py={3}>
      <Text variant="mainHeading" mb={6}>{t`Attendees`}</Text>
      <VStack gap={2}>
        {eventSignUpRows.map((item) =>
          isGroupEvent ? (
            <NewEventDetailAttendeesGroupListItem
              key={item.id}
              fragmentRef={item.groupFragmentRef}
              playerNounOverride={eventPlayerNoun}
            />
          ) : (
            <NewEventDetailAttendeesListItem
              key={item.id}
              fragmentRef={item.fragmentRef}
              playerNounOverride={eventPlayerNoun}
            />
          )
        )}
      </VStack>
    </Box>
  );
};

export const NewEventDetailAttendeesListFragment = graphql`
  fragment NewEventDetailAttendeesList on Event {
    id
    capacityType
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    eventSeries {
      facility {
        copyOverrides {
          eventPlayer {
            singular
          }
        }
      }
    }
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
          registrations {
            count
          }
          ...NewEventDetailAttendeesListItemFragment
          ...NewEventDetailAttendeesGroupListItemFragment
        }
      }
    }
  }
`;
